import type { PaginationEventHandlersParams } from '@ksyos/design-system/dist/components/pagination';
import {
    Avatar,
    Pagination,
    Radio,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
} from '@ksyos/design-system';
import { isEqual } from 'lodash';
import { useState } from 'react';
import { styled } from 'styled-components';
import type { PractitionerMatch } from '@common/interfaces/practitionerMatchType';
import type { AccountRequestMatch } from '@common/validation/accountRequest';
import { getDisplayInitials } from '@common/utils/getDisplayInitials';
import { getDisplayName } from '@common/utils/getDisplayName';
import { getSelectedLanguage } from '@frontend/translate';
import { AlertWithTitleAndDescription } from './AlertWithTitleAndDescription';

const StyledTableCell = styled(TableCell)`
    padding: 1em;
`;

type AlertText = {
    description?: string;
    title: string;
};

interface MatchingPractitionerTableSelectorProps {
    alertTextWithMatches: AlertText;
    alertTextWithoutMatches: AlertText;
    onSelectMatch: (result: AccountRequestMatch | null) => void;
    practitionerMatches: PractitionerMatch[];
}

export function MatchingPractitionerTableSelector(props: Readonly<MatchingPractitionerTableSelectorProps>) {
    const [selectedPractitionerMatch, setSelectedPractitionerMatch] = useState<PractitionerMatch | null>(null);
    const [itemsPerPage, setItemsPerPage] = useState<number>(10);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [shownPractitionerMatches, setShownPractitionerMatches] = useState<PractitionerMatch[]>(
        slicePractitionerMatches(itemsPerPage, pageNumber, props.practitionerMatches)
    );

    const translate = getSelectedLanguage();

    return (
        <>
            <AlertWithTitleAndDescription
                appearance="info"
                description={
                    props.practitionerMatches.length
                        ? props.alertTextWithMatches.description
                        : props.alertTextWithoutMatches.description
                }
                title={
                    props.practitionerMatches.length
                        ? props.alertTextWithMatches.title
                        : props.alertTextWithoutMatches.title
                }
            />
            {Boolean(props.practitionerMatches.length) && (
                <TableContainer sx={{ mb: 4 }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableHeader>{'  '}</TableHeader>
                                <TableHeader>{translate.common.name}</TableHeader>
                                <TableHeader>{translate.common.emailAddress}</TableHeader>
                                <TableHeader>{translate.organization.organization}</TableHeader>
                                <TableHeader>{translate.practitioner.identifierDeclaration}</TableHeader>
                                <TableHeader>{translate.common.Id}</TableHeader>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {renderMatchingPractitionerRows(selectedPractitionerMatch, shownPractitionerMatches)}
                        </TableBody>
                    </Table>
                    {props.practitionerMatches.length > 10 && (
                        <Pagination
                            onChange={(pageParams) => handlePageChange(pageParams, props.practitionerMatches)}
                            page={pageNumber}
                            pageSize={itemsPerPage}
                            totalItems={props.practitionerMatches.length}
                        />
                    )}
                </TableContainer>
            )}
        </>
    );

    function slicePractitionerMatches(pageSize: number, page: number, practitionerMatches: PractitionerMatch[]) {
        return practitionerMatches.slice(pageSize * (page - 1), pageSize * page);
    }

    function handlePageChange(
        { page, pageSize }: PaginationEventHandlersParams,
        practitionerMatches: PractitionerMatch[]
    ) {
        setPageNumber(page);
        setItemsPerPage(pageSize);
        setShownPractitionerMatches(slicePractitionerMatches(pageSize, page, practitionerMatches));
        setSelectedPractitionerMatch(null);
    }

    function handleSelectPractitioner(practitionerMatch: PractitionerMatch) {
        if (isEqual(selectedPractitionerMatch, practitionerMatch)) {
            setSelectedPractitionerMatch(null);

            props.onSelectMatch(null);
        } else {
            setSelectedPractitionerMatch(practitionerMatch);

            props.onSelectMatch({
                entity: practitionerMatch.practitionerId ? 'practitionerId' : 'matchingAccountRequestId',
                hasPractitionerEmailAddress: !!practitionerMatch.practitionerEmailAddress,
                id: practitionerMatch.practitionerId
                    ? practitionerMatch.practitionerId
                    : practitionerMatch.accountRequestId!,
            });
        }
    }

    function renderMatchingPractitionerRows(
        selectedMatchedPractitioner: PractitionerMatch | null,
        practitioners: PractitionerMatch[]
    ) {
        return practitioners.map((practitionerMatch) => (
            <TableRow
                key={practitionerMatch.practitionerId ?? practitionerMatch.accountRequestId}
                style={{ maxBlockSize: '1em' }}
            >
                <StyledTableCell>
                    <Radio
                        checked={isEqual(selectedMatchedPractitioner, practitionerMatch)}
                        onClick={() => handleSelectPractitioner(practitionerMatch)}
                        readOnly
                    />
                </StyledTableCell>
                <StyledTableCell ellipsis={true}>
                    <div style={{ alignItems: 'center', display: 'flex' }}>
                        <Avatar
                            initials={getDisplayInitials(practitionerMatch)}
                            size="xsmall"
                            style={{ marginRight: '8px' }}
                        />
                        {getDisplayName(practitionerMatch, false, 'familyNameCommaFirstName')}
                    </div>
                </StyledTableCell>
                <StyledTableCell ellipsis={true}>{practitionerMatch.emailAddress}</StyledTableCell>
                <StyledTableCell ellipsis={true}>{practitionerMatch.organizationName}</StyledTableCell>
                <StyledTableCell>{practitionerMatch?.identifierDeclaration}</StyledTableCell>
                <StyledTableCell>{practitionerMatch.uid}</StyledTableCell>
            </TableRow>
        ));
    }
}
